import { generatePath } from 'react-router-dom';
import { normalizeScreamingSnakeToUI } from '@dealstack/shared/lib/format/string';
import { normalizeCamelCaseToUI } from '@dealstack/shared/lib/format/normalizeCamelCaseToUI';
import { AUDITED_TABLE, isAuditedTable } from '@dealstack/shared/lib/common/business/dealEvent';
import {
	AccessEventAction,
	AuditAction,
	AuthEventAction,
	AuthEventMethod,
	DealEventCategory,
	HoldingEventType,
	ResourceType,
} from '@dealstack/shared/lib/backendClient';
import type {
	DealEventAccessTct,
	DealEventActorTct,
	DealEventAuthTct,
	DealEventCapitalTct,
	DealEventHoldingTct,
	DealEventLogUnreadRes,
	DealEventTct,
} from '@dealstack/shared/lib/backendClient';
import {
	extractDecimalPrecisionFromUnitDenominator,
	formatNumberToUI,
} from '@dealstack/shared/lib/format/number';
import {
	DATE_FORMATS,
	formatBEDateToUiWithFallback,
} from '@dealstack/shared/lib/format/dates/date.ts';
import {
	formatFieldName,
	formatTableNameToLabel,
} from '@dealstack/frontend/shared/components/Audit/AuditLayout/utils.ts';
import {
	DEAL_ROUTES,
	PEOPLE_ROUTES,
	ROUTES,
} from '@dealstack/frontend/app/pages/Routing/routes.ts';
import {
	securityDetailsPath,
	transactionDetailsPath,
} from '@dealstack/frontend/app/pages/Routing/routes.utils.ts';
import {
	DealEventLogTab,
	type DealEventMessageState,
} from '@dealstack/frontend/app/pages/Project/DealEventLog/DealEventLog.types.ts';
import { DEAL_EVENT_LOG_TAB_PARAM } from '@dealstack/frontend/app/pages/Project/DealEventLog/useDealEventLogTab.ts';

type Translate = (key: string) => string;

export const formatEventActionLabel = (action: AuditAction, t: Translate): string => {
	switch (action) {
		case AuditAction.CREATE:
			return t('deal_event_log.action.create');
		case AuditAction.UPDATE:
			return t('deal_event_log.action.update');
		case AuditAction.DELETE:
			return t('deal_event_log.action.delete');
		case AuditAction.RESTORE:
			return t('deal_event_log.action.restore');
		default:
			return action;
	}
};

/** Soft deletes carry both payloads, so the recorded action is the only thing that says so. */
export const isDeletionEvent = (event: DealEventTct): boolean =>
	event.action === AuditAction.DELETE;

const sourceDetailParts = (event: DealEventTct): { label: string; action: string } => {
	const prefix = `${event.source}_`;
	const detail = event.type.startsWith(prefix) ? event.type.slice(prefix.length) : event.type;

	return {
		label: normalizeScreamingSnakeToUI(event.source),
		action: normalizeScreamingSnakeToUI(detail),
	};
};

/** ShareAssociation.cost, Loan.amount and Transaction.cost are all Decimal(15, 2). */
export const MONEY_DECIMALS = 2;

/** A readable last resort for an identifier the audit maps do not label. */
export const humanizeIdentifier = (value: string): string =>
	normalizeScreamingSnakeToUI(normalizeCamelCaseToUI(value));

export const formatCategoryLabel = (category: DealEventCategory, t: Translate): string => {
	switch (category) {
		case DealEventCategory.CAPITAL:
			return t('deal_event_log.category.capital');
		case DealEventCategory.PARTICIPANT:
			return t('deal_event_log.category.participant');
		case DealEventCategory.ADMIN:
			return t('deal_event_log.category.admin');
		case DealEventCategory.MESSAGE:
			return t('deal_event_log.category.message');
		case DealEventCategory.ACCESS:
			return t('deal_event_log.category.access');
		default:
			return category;
	}
};

export const formatActorName = (actor: DealEventActorTct, t: Translate): string => {
	if (actor.isSystem) {
		return t('deal_event_log.actor.system');
	}

	return actor.displayName || actor.email || t('deal_event_log.actor.unknown');
};

export const formatChangedFields = (event: DealEventTct, t: Translate): string =>
	[
		...new Set(
			event.changedFields.map((field) => formatFieldName(field, t, event.entity.type))
		),
	].join(', ');

/** Units follow the security's denominator; money is always two decimals. */
export const formatCapitalUnits = (units: number, unitDenominator?: number): string =>
	formatNumberToUI(units, undefined, extractDecimalPrecisionFromUnitDenominator(unitDenominator));

export const formatCapitalCost = (cost: number, currency?: string): string =>
	formatNumberToUI(cost, currency, MONEY_DECIMALS);

export const formatCapitalDate = (date: string): string =>
	formatBEDateToUiWithFallback(date, DATE_FORMATS.CALENDAR_DATE);

const formatDatedLabel = (date: string, t: Translate): string =>
	`${t('deal_event_log.capital.dated')} ${formatCapitalDate(date)}`;

export const firstSeenEventIndex = (
	events: Pick<DealEventTct, 'occurredAt'>[],
	previousViewedAt: string | undefined
): number => {
	if (!previousViewedAt) {
		return -1;
	}
	const seenUntil = Date.parse(previousViewedAt);
	return events.findIndex((event) => Date.parse(event.occurredAt) <= seenUntil);
};

export const formatUnreadBadge = (
	unread: Pick<DealEventLogUnreadRes, 'count' | 'hasMore'> | undefined
): string | undefined => {
	const unreadCount = unread?.count;
	if (!unreadCount) {
		return undefined;
	}
	return unread.hasMore ? `${unreadCount}+` : String(unreadCount);
};

export const formatCapitalSummary = (capital: DealEventCapitalTct, t: Translate): string => {
	const parts: string[] = [];

	if (capital.securityName) {
		parts.push(capital.securityName);
	}

	if (capital.issuerName) {
		parts.push(capital.issuerName);
	}

	if (capital.units !== undefined) {
		const units = formatCapitalUnits(capital.units, capital.unitDenominator);

		parts.push(`${units} ${t('deal_event_log.capital.units')}`);
	}

	if (capital.cost !== undefined) {
		parts.push(formatCapitalCost(capital.cost, capital.currency));
	}

	if (capital.date) {
		parts.push(formatDatedLabel(capital.date, t));
	}

	return parts.join(' · ');
};

export const formatCapitalParties = (capital: DealEventCapitalTct): string =>
	[capital.fromName, capital.toName].filter(Boolean).join(' → ');

export const formatHoldingEventType = (eventType: HoldingEventType, t: Translate): string => {
	switch (eventType) {
		case HoldingEventType.ISSUANCE_OF_SHARE_CERTIFICATES:
			return t('holdings.event.issue');
		case HoldingEventType.PLEDGING_OF_SHARES:
			return t('holdings.event.pledge');
		case HoldingEventType.ASSIGNMENT_OF_VOTING_RIGHTS:
			return t('holdings.event.assigment');
		case HoldingEventType.NOTE:
			return t('holdings.event.note');
		default:
			return eventType;
	}
};

export const formatHoldingSummary = (holding: DealEventHoldingTct, t: Translate): string =>
	[
		formatHoldingEventType(holding.eventType, t),
		holding.securityName,
		formatDatedLabel(holding.date, t),
	]
		.filter(Boolean)
		.join(' · ');

const holdingCounterparty = (holding: DealEventHoldingTct, t: Translate): string | undefined => {
	switch (holding.eventType) {
		case HoldingEventType.PLEDGING_OF_SHARES:
			return [
				holding.pledgeToName,
				holding.pledgeAgainstName &&
					`(${t('holdings.events.against_desc')} ${holding.pledgeAgainstName})`,
			]
				.filter(Boolean)
				.join(' ');
		case HoldingEventType.ASSIGNMENT_OF_VOTING_RIGHTS:
			return holding.assignedToName;
		default:
			return undefined;
	}
};

export const formatHoldingParties = (holding: DealEventHoldingTct, t: Translate): string =>
	[holding.holderName, holdingCounterparty(holding, t)].filter(Boolean).join(' → ');

export const holdingDetailsPath = (dealId: string, holding: DealEventHoldingTct): string => {
	const path = generatePath(
		`${ROUTES.DEAL}${DEAL_ROUTES.PEOPLE}${PEOPLE_ROUTES.HOLDING_DETAILS}`,
		{
			dealId,
			personId: holding.holderId,
		}
	);
	const query = new URLSearchParams({
		securityId: holding.securityId,
		holderId: holding.holderId,
	});

	return `${path}?${query.toString()}`;
};

/**
 * The record's own page, where there is one. Gated on the record's live `deleted` flag rather
 * than on this event's action: a create is just as unreachable once a later event removed the
 * row, and a restore puts it back. Allocations and loans have no page of their own.
 */
export const dealEventRecordPath = (dealId: string, event: DealEventTct): string | undefined => {
	const { capital, holding } = event;

	if (holding) {
		return holding.deleted ? undefined : holdingDetailsPath(dealId, holding);
	}

	if (!capital || capital.deleted) {
		return undefined;
	}

	if (!capital.issuerId) {
		return undefined;
	}

	if (event.entity.type === AUDITED_TABLE.Transaction) {
		return transactionDetailsPath({
			dealId,
			personId: capital.issuerId,
			transactionId: event.entity.id,
		});
	}

	if (event.entity.type === AUDITED_TABLE.Security) {
		return securityDetailsPath({
			dealId,
			personId: capital.issuerId,
			securityId: capital.securityId ?? event.entity.id,
		});
	}

	return undefined;
};

/** The feed row of a message carries the EmailSent id it was built from. */
export const dealEventMessageId = (event: DealEventTct): string | undefined =>
	event.message ? event.sourceId : undefined;

export const dealEventMessagePath = (dealId: string, messageId: string): string =>
	generatePath(`${ROUTES.DEAL}${DEAL_ROUTES.EVENT_LOG_MESSAGE}`, { dealId, messageId });

export const dealEventLogPath = (dealId: string, search: string | undefined): string => {
	const path = generatePath(`${ROUTES.DEAL}${DEAL_ROUTES.EVENT_LOG}`, { dealId });

	return `${path}${search || `?${DEAL_EVENT_LOG_TAB_PARAM}=${DealEventLogTab.MESSAGING}`}`;
};

export const eventLogLinkState = (search: string): DealEventMessageState => ({
	eventLogSearch: search,
});

export const readEventLogSearch = (state: unknown): string | undefined =>
	typeof state === 'object' &&
	state !== null &&
	'eventLogSearch' in state &&
	typeof state.eventLogSearch === 'string'
		? state.eventLogSearch
		: undefined;

export const formatAccessHeading = (action: AccessEventAction, t: Translate): string => {
	switch (action) {
		case AccessEventAction.GRANT:
			return t('deal_event_log.access.granted');
		case AccessEventAction.REVOKE:
			return t('deal_event_log.access.revoked');
		default:
			return t('deal_event_log.access.changed');
	}
};

export const formatRoles = (roles: string[], tRole: Translate): string =>
	roles.map((role) => tRole(`permission-role.${role}`)).join(', ');

export const formatAccessSummary = (access: DealEventAccessTct, tRole: Translate): string => {
	const before = formatRoles(access.rolesBefore, tRole);
	const after = formatRoles(access.rolesAfter, tRole);
	const roles = before && after ? `${before} → ${after}` : after || before;

	return [access.targetName || access.targetEmail, access.programmeName, roles]
		.filter(Boolean)
		.join(' · ');
};

export const formatAccessResource = (
	access: DealEventAccessTct,
	tCommonUi: Translate
): string | undefined => {
	if (access.resourceType === ResourceType.PROGRAMME) {
		return access.programmeName ?? tCommonUi('title.programme');
	}

	return access.resourceType === ResourceType.DEAL ? tCommonUi('title.deal') : undefined;
};

export const formatAuthHeading = (action: AuthEventAction, t: Translate): string => {
	switch (action) {
		case AuthEventAction.SIGN_IN_SUCCEEDED:
			return t('deal_event_log.auth.sign_in_succeeded');
		case AuthEventAction.SIGN_IN_FAILED:
			return t('deal_event_log.auth.sign_in_failed');
		case AuthEventAction.MFA_SUCCEEDED:
			return t('deal_event_log.auth.mfa_succeeded');
		case AuthEventAction.EMAIL_VERIFIED:
			return t('deal_event_log.auth.email_verified');
		case AuthEventAction.RISK_DETECTED:
			return t('deal_event_log.auth.risk_detected');
		case AuthEventAction.PASSWORD_RESET_REQUESTED:
			return t('deal_event_log.auth.password_reset_requested');
		case AuthEventAction.PASSWORD_RESET_SUCCEEDED:
			return t('deal_event_log.auth.password_reset_succeeded');
		default:
			return action;
	}
};

export const formatAuthMethod = (method: AuthEventMethod, t: Translate): string => {
	switch (method) {
		case AuthEventMethod.PASSWORD:
			return t('deal_event_log.auth.method.password');
		case AuthEventMethod.OAUTH:
			return t('deal_event_log.auth.method.oauth');
		case AuthEventMethod.SSO:
			return t('deal_event_log.auth.method.sso');
		case AuthEventMethod.MAGIC_AUTH:
			return t('deal_event_log.auth.method.magic_auth');
		case AuthEventMethod.MFA:
			return t('deal_event_log.auth.method.mfa');
		case AuthEventMethod.EMAIL_VERIFICATION:
			return t('deal_event_log.auth.method.email_verification');
		default:
			return method;
	}
};

export const formatDealEventHeadingParts = (
	event: DealEventTct,
	t: Translate,
	tMessages: Translate,
	tCommonUi: Translate
): { label: string; action?: string; subject?: string } => {
	if (event.auth) {
		return { label: formatAuthHeading(event.auth.action, t) };
	}

	if (event.access) {
		return {
			label: formatAccessHeading(event.access.action, t),
			subject: formatAccessResource(event.access, tCommonUi),
		};
	}

	if (event.message) {
		return { label: tMessages('messageSent'), subject: event.message.subject };
	}

	// A row from outside the audit arms carries no audited table: those arms stamp every row
	// CREATE, so the action tells the reader nothing and the type has to carry it.
	return isAuditedTable(event.entity.type)
		? {
				label: formatTableNameToLabel(event.entity.type, t),
				action: formatEventActionLabel(event.action, t),
			}
		: sourceDetailParts(event);
};

export const formatDealEventName = (
	event: DealEventTct,
	t: Translate,
	tMessages: Translate,
	tCommonUi: Translate
): string => {
	const { label, action } = formatDealEventHeadingParts(event, t, tMessages, tCommonUi);

	return action
		? `${label} ${action.toLowerCase()}`
		: formatDealEventHeading(event, t, tMessages, tCommonUi);
};

export const formatDealEventHeading = (
	event: DealEventTct,
	t: Translate,
	tMessages: Translate,
	tCommonUi: Translate
): string => {
	const { label, action, subject } = formatDealEventHeadingParts(event, t, tMessages, tCommonUi);

	return [label, action, subject].filter(Boolean).join(' · ');
};

export const formatAuthSummary = (auth: DealEventAuthTct, t: Translate): string =>
	[auth.method && formatAuthMethod(auth.method, t), auth.errorCode].filter(Boolean).join(' · ');
